import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faCheckCircle,
  faExclamationTriangle, faCalendarAlt, faSync, faEraser
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import { FormationsContext } from '../layout/CunstomFormations';

const API_SESSION = `${API_CONFIG2.apiUrl}session/api_session.php`;
const API_FORMATION = `${API_CONFIG2.apiUrl}formation/api_formation.php`;

// ---- Dexie ----
const db = new Dexie('SessionsCache');
db.version(1).stores({ cache: 'key, timestamp' });
const CACHE_TTL = 24 * 60 * 60 * 1000;

// Clés de cache
const CACHE_SESSIONS = 'sessions_list';
const CACHE_FORMATIONS = 'formations_list';

// ---- Dédoublonnage des requêtes en cours ----
const pendingFetches = new Map();

const ManageSessions = () => {
  const { refreshKey, setSource } = useContext(FormationsContext) || {};

  const [sessions, setSessions] = useState([]);
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFormations, setLoadingFormations] = useState(false);
  const [filterFormation, setFilterFormation] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sessionData, setSessionData] = useState({
    id: '',
    id_formation: '',
    mois: '',
    debut: '',
    fin: ''
  });

  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // ----- Helpers cache -----
  const getCache = async (key) => {
    try {
      const cached = await db.cache.get(key);
      if (cached && cached.timestamp && (Date.now() - cached.timestamp) < CACHE_TTL) {
        return cached.data;
      }
      return null;
    } catch { return null; }
  };

  const setCache = async (key, data) => {
    try {
      await db.cache.put({ key, data, timestamp: Date.now() });
    } catch { /* ignore */ }
  };

  const getStaleCache = async (key) => {
    try {
      const cached = await db.cache.get(key);
      return cached ? cached.data : null;
    } catch { return null; }
  };

  // ----- Fonction pour vider le cache -----
  const clearCacheEntries = async () => {
    try {
      await db.cache.delete(CACHE_SESSIONS);
      await db.cache.delete(CACHE_FORMATIONS);
      // Supprimer également les caches avec préfixes (pour les filtres)
      const allKeys = await db.cache.toCollection().primaryKeys();
      const sessionKeys = allKeys.filter(key => key.startsWith('sessions_list_'));
      for (const key of sessionKeys) {
        await db.cache.delete(key);
      }
      if (isMounted.current) {
        showToast('Cache vidé avec succès', 'success', 3000);
      }
    } catch (error) {
      if (isMounted.current) {
        showToast('Erreur lors de la suppression du cache', 'danger');
      }
    }
  };

  // ----- Gestion des toasts -----
  const showToast = useCallback((message, variant = 'success', duration = 5000) => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      if (isMounted.current) setToast(prev => ({ ...prev, show: false }));
    }, duration);
  }, []);

  // ----- Récupération des formations (avec dédoublonnage + annulation) -----
  const fetchFormations = useCallback(async (force = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = CACHE_FORMATIONS;
    const requestKey = force ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage
    if (pendingFetches.has(requestKey) && !force) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setFormations(result);
          if (setSource) setSource('cache (partagé)');
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoadingFormations(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    // 1. Cache si non forcé
    if (!force) {
      const cached = await getCache(cacheKey);
      if (cached && isMounted.current) {
        setFormations(cached);
        if (setSource) setSource('cache');
        return;
      }
    }

    setLoadingFormations(true);
    const fetchPromise = (async () => {
      try {
        const res = await fetch(`${API_FORMATION}?action=fetch&limit=999`, { signal: controller.signal });

        // Gestion 429
        if (res.status === 429) {
          const stale = await getStaleCache(cacheKey);
          if (stale && isMounted.current) {
            setFormations(stale);
            if (setSource) setSource('cache (périmé)');
            showToast('429 – Utilisation du cache (formations)', 'warning');
            return stale;
          }
          // Sinon réessayer avec backoff
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchFormations(force, retryCount + 1);
        }

        if (!res.ok) {
          const stale = await getStaleCache(cacheKey);
          if (stale && isMounted.current) {
            setFormations(stale);
            if (setSource) setSource('cache (périmé)');
            showToast('Erreur réseau – Utilisation du cache formations', 'warning');
            return stale;
          }
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        if (data.status === 'success' && isMounted.current) {
          const list = data.data || [];
          setFormations(list);
          if (setSource) setSource(force ? 'live (forcé)' : 'live');
          await setCache(cacheKey, list);
          return list;
        } else {
          throw new Error(data.message || 'Erreur inconnue');
        }
      } catch (err) {
        if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
          // Annulation volontaire : on ne fait rien, le loading sera réinitialisé
          return null;
        }
        const stale = await getStaleCache(cacheKey);
        if (stale && isMounted.current) {
          setFormations(stale);
          if (setSource) setSource('cache (périmé)');
          showToast(`Erreur – Utilisation du cache formations : ${err.message}`, 'warning');
          return stale;
        }
        if (isMounted.current) {
          showToast(`Erreur chargement formations : ${err.message}`, 'danger');
        }
        throw err;
      } finally {
        if (isMounted.current) {
          setLoadingFormations(false);
        }
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
    }
  }, [showToast, setSource]);

  // ----- Récupération des sessions (avec dédoublonnage + annulation) -----
  const fetchSessions = useCallback(async (idFormation = '', force = false, retryCount = 0) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `${CACHE_SESSIONS}_${idFormation}`;
    const requestKey = force ? `${cacheKey}_force` : cacheKey;

    if (pendingFetches.has(requestKey) && !force) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setSessions(result);
          if (setSource) setSource('cache (partagé)');
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    if (!force) {
      const cached = await getCache(cacheKey);
      if (cached && isMounted.current) {
        setSessions(cached);
        if (setSource) setSource('cache');
        return;
      }
    }

    setLoading(true);
    const fetchPromise = (async () => {
      try {
        let url = `${API_SESSION}?action=list`;
        if (idFormation) url += `&id_formation=${idFormation}`;
        const res = await fetch(url, { signal: controller.signal });

        if (res.status === 429) {
          const stale = await getStaleCache(cacheKey);
          if (stale && isMounted.current) {
            setSessions(stale);
            if (setSource) setSource('cache (périmé)');
            showToast('429 – Utilisation du cache (sessions)', 'warning');
            return stale;
          }
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchSessions(idFormation, force, retryCount + 1);
        }

        if (!res.ok) {
          const stale = await getStaleCache(cacheKey);
          if (stale && isMounted.current) {
            setSessions(stale);
            if (setSource) setSource('cache (périmé)');
            showToast('Erreur réseau – Utilisation du cache sessions', 'warning');
            return stale;
          }
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        if (data.status === 'success' && isMounted.current) {
          const list = data.data || [];
          setSessions(list);
          if (setSource) setSource(force ? 'live (forcé)' : 'live');
          await setCache(cacheKey, list);
          return list;
        } else {
          throw new Error(data.message || 'Erreur inconnue');
        }
      } catch (err) {
        if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
          // Annulation volontaire
          return null;
        }
        const stale = await getStaleCache(cacheKey);
        if (stale && isMounted.current) {
          setSessions(stale);
          if (setSource) setSource('cache (périmé)');
          showToast(`Erreur – Utilisation du cache sessions : ${err.message}`, 'warning');
          return stale;
        }
        if (isMounted.current) {
          showToast(`Erreur chargement sessions : ${err.message}`, 'danger');
        }
        throw err;
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
    }
  }, [showToast, setSource]);

  // ----- Effets -----
  useEffect(() => {
    isMounted.current = true;
    // Chargement initial
    const init = async () => {
      await fetchFormations(false);
      await fetchSessions(filterFormation, false);
    };
    init();

    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      pendingFetches.clear();
    };
  }, []); // eslint-disable-line

  // Rafraîchir quand le filtre change (on force le rechargement pour avoir des données fraîches)
  useEffect(() => {
    if (filterFormation !== undefined) {
      fetchSessions(filterFormation, true);
    }
  }, [filterFormation, fetchSessions]);

  // Forcer le refresh via le contexte parent
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchFormations(true);
      fetchSessions(filterFormation, true);
    }
  }, [refreshKey, filterFormation, fetchFormations, fetchSessions]);

  // ----- Fonction pour vider le cache et forcer l'actualisation -----
  const clearCacheAndRefresh = async () => {
    if (loading || loadingFormations) return;
    await clearCacheEntries();
    // Après avoir vidé le cache, on force le rechargement
    await fetchFormations(true);
    await fetchSessions(filterFormation, true);
    if (isMounted.current) {
      showToast('Cache vidé et données rechargées', 'success', 3000);
    }
  };

  // ----- Fonctions CRUD -----
  const openCreateModal = () => {
    setEditing(false);
    setSessionData({
      id: '',
      id_formation: filterFormation || '',
      mois: '',
      debut: '',
      fin: ''
    });
    setShowModal(true);
  };

  const openEditModal = (session) => {
    setEditing(true);
    setSessionData({ ...session });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const action = editing ? 'update' : 'create';
    const method = editing ? 'PUT' : 'POST';
    const payload = editing ? { id: sessionData.id, ...sessionData } : { ...sessionData };
    if (!editing) delete payload.id;

    try {
      const res = await fetch(`${API_SESSION}?action=${action}`, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowModal(false);
        // Recharger avec force pour invalider le cache
        await fetchSessions(filterFormation, true);
        await fetchFormations(true);
      } else {
        showToast(data.message, 'danger');
      }
    } catch (error) {
      showToast('Erreur serveur', 'danger');
    }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API_SESSION}?action=delete&id=${sessionData.id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowDeleteModal(false);
        await fetchSessions(filterFormation, true);
        await fetchFormations(true);
      } else {
        showToast(data.message, 'danger');
      }
    } catch (error) {
      showToast('Erreur lors de la suppression', 'danger');
    }
  };

  const openDeleteModal = (session) => {
    setSessionData(session);
    setShowDeleteModal(true);
  };

  const getFormationName = (id) => {
    const f = formations.find(f => f.id === id);
    return f ? f.titre : `ID ${id}`;
  };

  // ----- Rendu -----
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – MANAGE SESSIONS (amélioré mobile)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .monetra-page {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: 24px;
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          border-color: #ef4444;
          color: #f87171;
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.12);
        }
        .monetra-btn.p-2 {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }

        .filter-wrapper {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: center;
        }
        .filter-wrapper .search-wrapper {
          flex: 1;
          min-width: 200px;
        }
        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 20px;
          transition: all 0.3s ease;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper select {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
          width: 100%;
        }
        .search-wrapper select option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }
        .glass-table th {
          padding: 12px 10px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
          font-weight: 600;
        }
        .glass-table td {
          padding: 10px 10px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .glass-table tr {
          transition: 0.2s;
        }
        .glass-table tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }

        .monetra-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          color: var(--text-primary);
          padding: 12px;
          width: 100%;
          transition: 0.3s;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.06);
        }
        .monetra-input::placeholder {
          color: var(--text-muted);
        }
        .monetra-input option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .toast-monetra {
          background: rgba(10, 20, 45, 0.8) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-danger { color: #ef4444; }
        .text-primary { color: var(--accent-blue); }
        .fw-bold { font-weight: 600; }
        .small { font-size: clamp(0.65rem, 0.8vw, 0.8rem); }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 1rem; }
        .px-3 { padding-left: 1rem; padding-right: 1rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .flex-grow-1 { flex-grow: 1; }
        .text-end { text-align: right; }
        .text-center { text-align: center; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .overflow-hidden { overflow: hidden; }
        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .w-100 { width: 100%; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .border-opacity-10 { opacity: 0.1; }

        /* ============================================================
           RESPONSIVE MOBILE
           ============================================================ */
        @media (max-width: 768px) {
          .monetra-page { padding: 12px; }
          .glass-card { padding: 1rem; border-radius: 16px; }

          .d-flex.justify-content-between.align-items-center {
            flex-direction: column;
            align-items: stretch !important;
            gap: 12px;
          }
          .d-flex.justify-content-between.align-items-center > div:first-child {
            text-align: center;
          }

          .filter-wrapper {
            flex-direction: column;
            align-items: stretch;
          }
          .filter-wrapper .search-wrapper {
            max-width: 100%;
          }
          .filter-wrapper .monetra-btn {
            justify-content: center;
          }

          .glass-table th, .glass-table td {
            padding: 6px 8px;
            font-size: 0.7rem;
          }
          .glass-table th:last-child, .glass-table td:last-child {
            min-width: 80px;
          }

          .monetra-btn {
            font-size: 0.75rem;
            padding: 6px 12px;
            min-height: 38px;
          }
          .monetra-btn.p-2 {
            padding: 4px 8px;
            min-width: 32px;
          }

          .monetra-modal .modal-dialog {
            margin: 0.5rem;
          }
          .monetra-modal .modal-content {
            border-radius: 20px;
          }
          .monetra-modal .modal-footer {
            flex-direction: column-reverse;
          }
          .monetra-modal .modal-footer button {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .monetra-btn {
            font-size: 0.7rem;
            padding: 4px 10px;
            min-height: 34px;
          }
          .glass-table th, .glass-table td {
            font-size: 0.6rem;
            padding: 4px 6px;
          }
        }
      `}</style>

      <div className="monetra-page">
        {/* En-tête */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="fw-bold mb-1" style={{ fontSize: 'clamp(1.4rem, 4vw, 2.2rem)' }}>
              Gestion des Sessions
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Planifiez et gérez les sessions de formation.
            </p>
          </div>
          <button className="monetra-btn primary" onClick={openCreateModal}>
            <FontAwesomeIcon icon={faPlus} /> Ajouter une session
          </button>
        </div>

        {/* Filtre par formation + boutons refresh */}
        <div className="glass-card mb-4">
          <div className="filter-wrapper">
            <div className="search-wrapper">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-secondary" />
              <select
                value={filterFormation}
                onChange={(e) => setFilterFormation(e.target.value)}
                disabled={loadingFormations}
              >
                <option value="">Toutes les formations</option>
                {formations.map(f => (
                  <option key={f.id} value={f.id}>{f.titre}</option>
                ))}
              </select>
            </div>
            <button
              className="monetra-btn"
              onClick={() => {
                fetchSessions(filterFormation, true);
                fetchFormations(true);
              }}
              disabled={loading || loadingFormations}
            >
              <FontAwesomeIcon icon={faSync} spin={loading || loadingFormations} /> Rafraîchir
            </button>
            <button
              className="monetra-btn danger"
              onClick={clearCacheAndRefresh}
              disabled={loading || loadingFormations}
              title="Vider le cache et forcer l'actualisation"
            >
              <FontAwesomeIcon icon={faEraser} /> Vider le cache
            </button>
          </div>
        </div>

        {/* Tableau des sessions */}
        <div className="glass-card p-0 overflow-hidden">
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Formation</th>
                  <th>Mois</th>
                  <th>Début</th>
                  <th>Fin</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></td></tr>
                ) : sessions.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-5 text-secondary">Aucune session trouvée.</td></tr>
                ) : (
                  sessions.map(s => (
                    <tr key={s.id}>
                      <td><span className="fw-bold text-secondary">{s.id}</span></td>
                      <td><strong>{getFormationName(s.id_formation)}</strong></td>
                      <td><span className="text-secondary">{s.mois}</span></td>
                      <td><span className="text-secondary">{s.debut}</span></td>
                      <td><span className="text-secondary">{s.fin}</span></td>
                      <td className="text-end">
                        <button className="monetra-btn p-2 me-1" onClick={() => openEditModal(s)} title="Modifier">
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button className="monetra-btn danger p-2" onClick={() => openDeleteModal(s)} title="Supprimer">
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-top border-secondary border-opacity-10 text-secondary small">
            {sessions.length} session(s) affichée(s)
          </div>
        </div>
      </div>

      {/* Modale d'ajout / modification */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={editing ? faEdit : faPlus} className="text-primary me-2" />
              {editing ? 'Modifier la session' : 'Ajouter une session'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-12">
                <label className="text-secondary small">Formation</label>
                <select
                  className="monetra-input"
                  value={sessionData.id_formation}
                  onChange={(e) => setSessionData({ ...sessionData, id_formation: e.target.value })}
                  required
                >
                  <option value="">Sélectionner une formation</option>
                  {formations.map(f => (
                    <option key={f.id} value={f.id}>{f.titre}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="text-secondary small">Mois</label>
                <select
                  className="monetra-input"
                  value={sessionData.mois}
                  onChange={(e) => setSessionData({ ...sessionData, mois: e.target.value })}
                  required
                >
                  <option value="">Choisir...</option>
                  {['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'].map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="text-secondary small">Date de début</label>
                <input
                  type="date"
                  className="monetra-input"
                  value={sessionData.debut}
                  onChange={(e) => setSessionData({ ...sessionData, debut: e.target.value })}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="text-secondary small">Date de fin</label>
                <input
                  type="date"
                  className="monetra-input"
                  value={sessionData.fin}
                  onChange={(e) => setSessionData({ ...sessionData, fin: e.target.value })}
                  required
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} className="monetra-btn outline-secondary">Annuler</Button>
            <Button variant="success" type="submit" className="monetra-btn primary">
              {editing ? 'Mettre à jour' : 'Créer'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modale de suppression */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger"><FontAwesomeIcon icon={faExclamationTriangle} /> Confirmation de suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer la session du <strong>{sessionData.mois}</strong> pour la formation <strong>{getFormationName(sessionData.id_formation)}</strong> ?
          <br /><span className="text-secondary small">Cette action est irréversible.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="monetra-btn outline-secondary">Annuler</Button>
          <Button variant="danger" onClick={handleDelete} className="monetra-btn danger">Confirmer la suppression</Button>
        </Modal.Footer>
      </Modal>

      {/* Toast */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <div className="toast-monetra p-3" style={{ minWidth: 'clamp(200px, 30vw, 300px)' }}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
              />
              <span style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)' }}>{toast.message}</span>
              <button
                className="btn-close-custom ms-auto"
                onClick={() => setToast({ ...toast, show: false })}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', opacity: 0.5, fontSize: '1.2rem' }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageSessions;